import { useCallback, useMemo } from 'react';
import {
  useGetUserContextQuery,
} from '@/shared/features/user-context/infra/adapters/userContextApiSlice';
import {
  useAddToWishlistMutation,
  useRemoveFromWishlistMutation,
} from '../../infra/adapters/wishlist.adapter';
import { useAppDispatch } from '@/app/store/store';
import { openLoginPopup, setPendingWishlistDesignId } from '@/app/store/loginUiSlice';
import { syncWishlistUserContext } from '../utils/syncWishlistUserContext';

const EMPTY_WISHLIST: number[] = [];

export function useWishlistToggle() {
  const dispatch = useAppDispatch();

  const { data: userContext } = useGetUserContextQuery(undefined, {
    refetchOnMountOrArgChange: false,
  });

  const [addToWishlist, { isLoading: isAdding }] = useAddToWishlistMutation();
  const [removeFromWishlist, { isLoading: isRemoving }] = useRemoveFromWishlistMutation();

  const rawWishListDesignIds =
    (userContext as Record<string, unknown> | undefined)?.wishListDesignIds as number[] | undefined;
  const wishListDesignIds = useMemo(
    () => rawWishListDesignIds ?? EMPTY_WISHLIST,
    [rawWishListDesignIds]
  );
  const isLoggedIn =
    (userContext as Record<string, unknown> | undefined)?.isLoggedIn === true;
  const isSoftLoggedIn =
    (userContext as Record<string, unknown> | undefined)?.isSoftLoggedIn === true;
  const canUseWishlist = isLoggedIn || isSoftLoggedIn;

  const isWishlisted = useCallback(
    (designId: number) => wishListDesignIds.includes(designId),
    [wishListDesignIds]
  );

  const toggleWishlist = useCallback(
    async (designId: number): Promise<boolean> => {
      if (!canUseWishlist) {
        dispatch(setPendingWishlistDesignId(designId));
        dispatch(openLoginPopup('otp'));
        return false;
      }

      const wishlisted = wishListDesignIds.includes(designId);
      try {
        const result = wishlisted
          ? await removeFromWishlist(designId).unwrap()
          : await addToWishlist(designId).unwrap();

        syncWishlistUserContext(dispatch, result);
        return true;
      } catch {
        // mutation failed – leave cache unchanged
        return false;
      }
    },
    [canUseWishlist, wishListDesignIds, addToWishlist, removeFromWishlist, dispatch]
  );

  return { isWishlisted, toggleWishlist, isLoading: isAdding || isRemoving };
}
